import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";

const SCALP_FILTERS = [
  { key: "", label: "All" },
  { key: "OPEN", label: "Open", type: "status" },
  { key: "CLOSED", label: "Closed", type: "status" },
  { key: "TARGET", label: "Target", type: "reason" },
  { key: "STOP", label: "Stop", type: "reason" },
];

const SWING_FILTERS = [
  { key: "", label: "All" },
  { key: "HOLDING", label: "Holding" },
  { key: "CLOSED", label: "Closed" },
  { key: "TARGET", label: "Target" },
  { key: "STOP", label: "Stop" },
  { key: "BREAKEVEN", label: "B/E" },
  { key: "MANUAL", label: "Manual" },
];

const reasonColor = {
  TARGET: "#00E676", STOP: "#FF3B30", TIME: "#FFB300", MANUAL: "#007AFF",
  KILL: "#FF2400", BREAKEVEN: "#14B8A6", EXTERNAL_EXIT: "#8A919E", PARTIAL_EXTERNAL: "#8A919E",
};

// ---- SCALP (MIS) journal ----
function ScalpJournal() {
  const [filter, setFilter] = useState({ key: "", type: null });
  const [trades, setTrades] = useState([]);

  const load = () => {
    const params = {};
    if (filter.type === "status") params.status = filter.key;
    if (filter.type === "reason") params.reason = filter.key;
    api.journal(params).then((d) => setTrades(d.trades || []));
  };
  useEffect(load, [filter]);
  useEffect(() => {
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [filter]);

  return (
    <>
      <Header count={trades.length} subtitle="SCALP · MIS intraday" />
      <div className="px-4 py-3 flex gap-1.5 overflow-x-auto no-scrollbar">
        {SCALP_FILTERS.map((f) => (
          <FilterBtn key={f.label} label={f.label}
            active={filter.key === f.key && (filter.type || null) === (f.type || null)}
            onClick={() => setFilter({ key: f.key, type: f.type })} />
        ))}
      </div>
      <div className="px-4 space-y-2" data-testid="journal-list">
        {trades.map((t) => {
          const closed = t.status === "CLOSED";
          const pnl = t.net_pnl || 0;
          const pos = pnl >= 0;
          return (
            <div key={t.id} className="bg-surface border border-edge rounded-sm p-3" data-testid={`journal-row-${t.symbol}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{t.symbol}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-elevated border border-edge text-muted2 font-bold tracking-wider">{t.mode}</span>
                  {t.exit_reason && (
                    <span className="text-[9px] font-bold tracking-wider" style={{ color: reasonColor[t.exit_reason] }}>{t.exit_reason}</span>
                  )}
                  {!closed && <span className="text-[9px] font-bold tracking-wider text-brand pulse-dot">● OPEN</span>}
                </div>
                {closed && (
                  <span className="mono text-sm font-bold" style={{ color: pos ? "#00E676" : "#FF3B30" }}>{fmtINR(pnl)}</span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 mono text-[10px]">
                <div><div className="text-muted2">Qty</div><div className="text-white">{t.qty}</div></div>
                <div><div className="text-muted2">Entry</div><div className="text-white">{fmtINR(t.entry_price, 1)}</div></div>
                <div><div className="text-muted2">Exit</div><div className="text-white">{t.exit_price ? fmtINR(t.exit_price, 1) : "—"}</div></div>
                <div><div className="text-muted2">R</div><div className="text-white">{closed ? t.r_multiple : "—"}</div></div>
              </div>
              <div className="flex justify-between mono text-[10px] text-muted2 mt-1.5">
                <span>mom {t.momentum_pct >= 0 ? "+" : ""}{t.momentum_pct}%</span>
                {closed && <span>cost {fmtINR(t.costs, 0)}</span>}
                <span>{(t.entry_time || "").slice(5, 16).replace("T", " ")}</span>
              </div>
            </div>
          );
        })}
        {trades.length === 0 && <EmptyRow />}
      </div>
    </>
  );
}

// ---- SWING (CNC) journal ----
function SwingJournal() {
  const [filter, setFilter] = useState("");
  const [d, setD] = useState(null);

  const load = () => api.swing().then(setD).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const open = (d?.positions || []).map((p) => ({
    key: `o-${p.symbol}`, symbol: p.symbol, status: "OPEN", state: p.state,
    qty: p.qty, entry: p.entry_price, exit: null, reason: null,
    net: p.pnl, pct: p.pnl_pct, target: p.target_price, stop: p.stop_price, peak: p.peak_price,
    basis: null, time: p.bought_date,
  }));
  const closed = (d?.closed || []).map((t, i) => ({
    key: `c-${i}-${t.symbol}`, symbol: t.symbol, status: "CLOSED", state: "CLOSED",
    qty: t.filled_qty || t.qty, entry: t.avg_price, exit: t.exit_price, reason: t.exit_reason,
    net: t.net_pnl, gross: t.gross_pnl, costs: t.costs, basis: t.cost_basis,
    time: (t.exit_time || t.created_at || "").slice(5, 16).replace("T", " "),
  }));
  const all = [...open, ...closed];
  const rows = all.filter((r) => {
    if (!filter) return true;
    if (filter === "HOLDING") return r.status === "OPEN";
    if (filter === "CLOSED") return r.status === "CLOSED";
    return r.reason === filter; // TARGET / STOP / MANUAL
  });

  return (
    <>
      <Header count={rows.length} subtitle="SWING · CNC delivery" />
      <div className="px-4 py-3 flex gap-1.5 overflow-x-auto no-scrollbar">
        {SWING_FILTERS.map((f) => (
          <FilterBtn key={f.label} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
        ))}
      </div>
      <div className="px-4 space-y-2" data-testid="journal-list">
        {rows.map((r) => {
          const closed = r.status === "CLOSED";
          const win = (r.net || 0) >= 0;
          return (
            <div key={r.key} className="bg-surface border border-edge rounded-sm p-3" data-testid={`journal-row-${r.symbol}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{r.symbol}</span>
                  {r.basis && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-elevated border border-edge text-muted2 font-bold tracking-wider">
                      {r.basis === "INTRADAY" ? "INTRA" : "DELIVERY"}
                    </span>
                  )}
                  {r.reason && (
                    <span className="text-[9px] font-bold tracking-wider" style={{ color: reasonColor[r.reason] || "#8A919E" }}>{r.reason}</span>
                  )}
                  {!closed && <span className="text-[9px] font-bold tracking-wider text-brand pulse-dot">● {r.state}</span>}
                </div>
                <span className="mono text-sm font-bold" style={{ color: win ? "#00E676" : "#FF3B30" }}>
                  {closed ? "" : (win ? "+" : "")}{fmtINR(r.net || 0)}{!closed && r.pct != null ? ` (${r.pct >= 0 ? "+" : ""}${r.pct}%)` : ""}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 mono text-[10px]">
                <div><div className="text-muted2">Qty</div><div className="text-white">{r.qty}</div></div>
                <div><div className="text-muted2">Entry</div><div className="text-white">{r.entry ? fmtINR(r.entry, 1) : "—"}</div></div>
                <div><div className="text-muted2">{closed ? "Exit" : "Peak"}</div><div className="text-white">{closed ? (r.exit ? fmtINR(r.exit, 1) : "—") : (r.peak ? fmtINR(r.peak, 1) : "—")}</div></div>
                <div><div className="text-muted2">{closed ? "Charges" : "Trail"}</div><div className="text-white">{closed ? fmtINR(r.costs || 0, 0) : (r.stop ? fmtINR(r.stop, 1) : "—")}</div></div>
              </div>
              <div className="flex justify-between mono text-[10px] text-muted2 mt-1.5">
                {closed ? <span>gross {fmtINR(r.gross || 0, 0)}</span> : <span>live position</span>}
                <span>{r.time}</span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <EmptyRow />}
      </div>
    </>
  );
}

function Header({ count, subtitle }) {
  return (
    <div className="glass sticky top-0 z-30 border-b border-edge px-4 py-3">
      <h1 className="text-xl font-bold tracking-tight">Trade Journal</h1>
      <p className="text-[11px] text-muted2">{count} trades · {subtitle}</p>
    </div>
  );
}

function FilterBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`filter-${label.toLowerCase()}`}
      className={`px-3 py-1.5 rounded-sm text-[11px] font-bold tracking-wider whitespace-nowrap border ${
        active ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"
      }`}
    >
      {label}
    </button>
  );
}

function EmptyRow() {
  return <div className="border border-dashed border-edge rounded-sm py-10 text-center text-xs text-muted2">No trades match this filter.</div>;
}

export default function Journal() {
  const [strategy, setStrategy] = useState(null);
  useEffect(() => {
    api.getConfig().then((c) => setStrategy(c.strategy || "SCALP")).catch(() => setStrategy("SCALP"));
  }, []);

  if (!strategy) return <div className="p-6 text-muted2 text-sm">Loading journal…</div>;
  return (
    <div className="pb-40">
      {strategy === "SWING" ? <SwingJournal /> : <ScalpJournal />}
    </div>
  );
}
